/**
 * The session folder, replacing `POST /api/session` and `/session/:key/archive`.
 *
 * The agent created `<target>\<sessionKey>\`, copied the selected photos into
 * it and wrote the composites beside them. It does the same here through a
 * directory handle, with one change of meaning: that folder used to be inside
 * Google Drive for Desktop's sync root and was HOW the photos reached the
 * guest. It no longer is — raia-api receives the photos directly (see raia.ts)
 * — so this folder is now purely the operator's local archive of the evening.
 *
 * Which makes it optional, and it is treated that way: a console with no target
 * folder granted still prints and still uploads. The desktop app would have
 * thrown; here the operator simply has no local copy.
 *
 * The folder is still created at print time rather than when the flow starts,
 * so backing out of a step leaves nothing on disk (§5a).
 */

import { sanitizeFileName } from '@/core/session'
import { subdirectory, writeFile } from './fs'

export interface SessionFile {
  name: string
  blob: Blob
}

/**
 * Writes one session's files into `<target>/<sessionKey>/`.
 *
 * Returns the number written. Never throws for a single bad file: a print has
 * already happened by the time this runs, and failing the whole archive because
 * one name collided would lose the rest of it.
 */
export async function writeSessionFolder(
  target: FileSystemDirectoryHandle | null,
  sessionKey: string,
  files: SessionFile[],
): Promise<{ written: number; failed: string[] }> {
  if (!target) return { written: 0, failed: [] }

  const folder = await subdirectory(target, sanitizeFileName(sessionKey))

  let written = 0
  const failed: string[] = []

  for (const file of files) {
    try {
      await writeFile(folder, sanitizeFileName(file.name), file.blob)
      written += 1
    } catch {
      failed.push(file.name)
    }
  }

  return { written, failed }
}

/** Adds one more file to a session already on disk — the video, once encoded. */
export async function appendSessionFile(
  target: FileSystemDirectoryHandle | null,
  sessionKey: string,
  file: SessionFile,
): Promise<boolean> {
  if (!target) return false
  try {
    const folder = await subdirectory(target, sanitizeFileName(sessionKey))
    await writeFile(folder, sanitizeFileName(file.name), file.blob)
    return true
  } catch {
    return false
  }
}
